/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback } from 'react';

import { URL_SHORTENER_PREFIX } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/components/MessageInputV0/components/WhatsAppTemplateInputs/hooks/useWhatsAppVariableProcessor';
import {
  TemplateKey,
  TemplateType,
  WhatsAppAction,
} from '@/src/modules/CampaignsModule/interfaces/WhatsApp';

export interface IParsedContent {
  isValid: boolean;
  body: string;
  contentSid?: string;
  mediaUrl?: string | string[];
  actions?: WhatsAppAction[] | unknown;
  header_text?: string;
  footer?: string;
  types?: {
    [key in TemplateKey]?: TemplateType;
  };
  error?: Error;
}

/**
 * Centralizes the transformation of Twilio-type variables:
 * 1) Builds JSON from markers like ${1#Name} => {{1}}
 * 2) Analyzes and replaces markers ({{1}}, {{2}}, etc.) if we receive a JSON string
 * Compatible with both old and new template formats
 */
export function useContentVariables() {
  const transformToTwilioPlaceholders = useCallback((message: string) => {
    const variableRegex = /\$\{\d+#[^}]+\}/g;
    const customVariables: Record<string, string> = {};
    let index = 1;

    const updatedContent = message.replace(variableRegex, (match) => {
      const twilioVar = `{{${index}}}`;
      customVariables[`variable${index}`] = match;
      index++;
      return twilioVar;
    });

    return { updatedContent, customVariables };
  }, []);

  const parseAndReplaceContentVariables = useCallback((jsonString: string): IParsedContent => {
    if (!jsonString || !jsonString.trim()) {
      return { isValid: false, body: '' };
    }

    try {
      const parsed = JSON.parse(jsonString);

      if (parsed.types && typeof parsed.types === 'object') {
        const result: IParsedContent = {
          isValid: true,
          body: '',
          contentSid: parsed.ContentSid,
          types: {},
        };

        Object.entries(parsed.types).forEach(([key, template]) => {
          if (template && typeof template === 'object') {
            const templateKey = key as TemplateKey;

            const templateCopy = { ...template } as TemplateType;

            if ('body' in templateCopy && typeof templateCopy.body === 'string') {
              if (parsed.variables && typeof parsed.variables === 'object') {
                Object.entries(parsed.variables).forEach(([varKey, varValue]) => {
                  const placeholder = `{{${varKey}}}`;
                  const valueString = String(varValue);
                  const shortenerPlaceholder = `${URL_SHORTENER_PREFIX}${placeholder}`;

                  if (templateCopy.body.includes(shortenerPlaceholder)) {
                    templateCopy.body = templateCopy.body.replaceAll(
                      shortenerPlaceholder,
                      valueString
                    );
                  } else {
                    templateCopy.body = templateCopy.body.replaceAll(placeholder, valueString);
                  }
                });
              }
            }

            if (!result.types) {
              result.types = {};
            }

            result.types[templateKey] = templateCopy;
          }
        });

        if (result.types && result.types['twilio/text']) {
          result.body = (result.types['twilio/text'] as { body: string }).body;
        } else if (result.types && result.types['twilio/media']) {
          result.body = (result.types['twilio/media'] as { body: string }).body;
          result.mediaUrl = (result.types['twilio/media'] as { media: string[] }).media;
        } else if (result.types && result.types['whatsapp/card']) {
          const cardTemplate = result.types['whatsapp/card'] as {
            body: string;
            media: string[];
            header_text: string;
            footer: string;
            actions: WhatsAppAction[];
          };

          result.body = cardTemplate.body;
          result.mediaUrl = cardTemplate.media;
          result.header_text = cardTemplate.header_text;
          result.footer = cardTemplate.footer;
          result.actions = cardTemplate.actions;
        }

        return result;
      }

      if (!parsed.body) {
        return { isValid: false, body: '' };
      }

      let replacedMessage = parsed.body;
      if (parsed.ContentVariables && typeof parsed.ContentVariables === 'object') {
        const variablesArray = Object.values(parsed.ContentVariables);
        variablesArray.forEach((value, index) => {
          const placeholder = `{{${index + 1}}}`;
          const valueString = String(value);
          const shortenerPlaceholder = `${URL_SHORTENER_PREFIX}${placeholder}`;

          if (replacedMessage.includes(shortenerPlaceholder)) {
            replacedMessage = replacedMessage.replaceAll(shortenerPlaceholder, valueString);
          } else {
            replacedMessage = replacedMessage.replaceAll(placeholder, valueString);
          }
        });
      }

      return {
        isValid: true,
        body: replacedMessage,
        contentSid: parsed.ContentSid,
        mediaUrl: parsed.mediaUrl,
        actions: parsed.actions,
      };
    } catch (error) {
      return { isValid: false, body: '', error: error as Error };
    }
  }, []);

  return {
    transformToTwilioPlaceholders,
    parseAndReplaceContentVariables,
  };
}
